import { SITE_ORIGIN } from "./site";

const SITE_NAME = "Rencin Gifting";

export const DEFAULT_SEO = {
  title: `${SITE_NAME} | Curated Gift Boxes and Festive Hampers`,
  description:
    "Shop curated gift boxes, festive hampers, and personalised gifting from Rencin Gifting. Secure checkout, tracked shipping across India, and thoughtful delivery.",
  path: "/",
  index: true,
};

/** Per-route SEO overrides. Keys are exact paths or path prefixes. */
export const ROUTE_SEO = {
  "/": {
    title: `${SITE_NAME} | Curated Gift Boxes and Festive Hampers`,
    description:
      "Discover premium gift boxes, festive hampers, and personalised gifting from Rencin Gifting. Shop online with secure UPI checkout and delivery across India.",
    path: "/",
  },
  "/shop": {
    title: "Shop Gift Boxes",
    description:
      "Browse curated gift boxes and festive hampers from Rencin Gifting. Filter by occasion, price, and style — delivered across India.",
    path: "/shop",
  },
  "/about": {
    title: "About Us",
    description:
      "Learn about Rencin Gifting — our story, mission, and passion for curated gift boxes and thoughtful delivery across India.",
    path: "/about",
  },
  "/privacy": {
    title: "Privacy Policy",
    description:
      "Read the Rencin Gifting privacy policy. Learn how we collect, use, and protect your personal information when you shop or contact us.",
    path: "/privacy",
  },
  "/shipping": {
    title: "Shipping Policy",
    description:
      "Rencin Gifting shipping policy — delivery areas, rates, dispatch times, tracking, and support for orders shipped across India.",
    path: "/shipping",
  },
  "/returns": {
    title: "Return & Refund Policy",
    description:
      "Rencin Gifting return and refund policy — eligibility, personalised orders, how to request a return, and refund timelines.",
    path: "/returns",
  },
  "/catalogue": {
    title: "Assembly Instructions",
    description:
      "Explore Rencin Gifting assembly instructions — step-by-step guide for folding, sealing, and tying your gift box.",
    path: "/catalogue",
  },
  "/gift-guide": {
    title: "Gift Guide",
    description:
      "Gift guide from Rencin Gifting — tips for choosing, personalising, and caring for curated gift boxes and hampers.",
    path: "/gift-guide",
  },
  "/cart": {
    title: "Your Cart",
    description: "Review items in your Rencin Gifting cart before checkout.",
    path: "/cart",
    index: false,
  },
  "/checkout": {
    title: "Checkout",
    description: "Complete your Rencin Gifting order with secure checkout.",
    path: "/checkout",
    index: false,
  },
  "/login": {
    title: "Sign In",
    description: "Sign in to your Rencin Gifting account to view orders.",
    path: "/login",
    index: false,
  },
  "/orders": {
    title: "Order History",
    description: "View your Rencin Gifting order history and tracking.",
    path: "/orders",
    index: false,
  },
  "/admin": {
    title: "Admin",
    path: "/admin",
    index: false,
  },
};

const NOINDEX_PREFIXES = ["/checkout", "/admin", "/customise", "/product/"];

export function resolvePageSeo(pathname) {
  if (ROUTE_SEO[pathname]) {
    return { ...DEFAULT_SEO, ...ROUTE_SEO[pathname] };
  }

  if (pathname.startsWith("/product/")) {
    return {
      ...DEFAULT_SEO,
      title: "Product Details",
      description:
        "View gift box details, images, and ordering options from Rencin Gifting.",
      path: pathname,
      index: false,
    };
  }

  if (pathname.startsWith("/customise")) {
    return {
      ...DEFAULT_SEO,
      title: "Customise Gift Box",
      path: pathname,
      index: false,
    };
  }

  if (pathname === "/saree-guide") {
    return resolvePageSeo("/gift-guide");
  }

  return {
    ...DEFAULT_SEO,
    path: pathname,
    index: !NOINDEX_PREFIXES.some((prefix) => pathname.startsWith(prefix)),
  };
}

/** Normalize pathname for canonical URLs (no trailing slash except root). */
export function canonicalPath(pathname) {
  const path = String(pathname || "/").trim() || "/";
  if (path === "/") return "/";
  return path.replace(/\/+$/, "") || "/";
}

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applyPageSeo(pathname) {
  const seo = resolvePageSeo(pathname);
  const canonical = `${SITE_ORIGIN}${canonicalPath(seo.path || pathname)}`;
  const fullTitle =
    seo.title && seo.title.includes(SITE_NAME)
      ? seo.title
      : seo.title
        ? `${seo.title} | ${SITE_NAME}`
        : DEFAULT_SEO.title;

  document.title = fullTitle;
  upsertMeta("name", "description", seo.description || DEFAULT_SEO.description);
  upsertMeta("name", "robots", seo.index === false ? "noindex, nofollow" : "index, follow");
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:title", fullTitle);
  upsertMeta("property", "og:description", seo.description || DEFAULT_SEO.description);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:image", `${SITE_ORIGIN}/logo.png`);
  upsertMeta("name", "twitter:image", `${SITE_ORIGIN}/logo.png`);
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", fullTitle);
  upsertMeta("name", "twitter:description", seo.description || DEFAULT_SEO.description);
  upsertCanonical(canonical);

  if (seo.index !== false && ["/privacy", "/shipping", "/about", "/gift-guide"].includes(pathname)) {
    upsertJsonLd("page-seo-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: fullTitle,
      description: seo.description,
      url: canonical,
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_ORIGIN,
      },
    });
  } else {
    const el = document.getElementById("page-seo-jsonld");
    if (el) el.remove();
  }
}
